/**
 * G21 Floor Plans – stylized SVG diagrams in the brand language.
 * Reflects the actual room arrangement of the original CAD plans:
 *   • EG  – stacked rooms (Arbeit/Schlafen/Küche) on the west wall,
 *           Wohnen/Essen on the east, Terrasse extending eastward.
 *   • OG  – Schlafen + Abstell + Küche + Kinderzimmer on west wall,
 *           Treppenhaus + Bäder central, two more bedrooms east,
 *           Wohnen/Essen south, Balkon south-east.
 *   • DG  – Schlafen NW, Arbeiten NE (both with N facing windows),
 *           central hallway with en-suite Bad + WC + Treppenhaus,
 *           large open Wohnen/Essen with integrated Küche & Kamin
 *           south-east, Dachterrasse SE corner.
 * Editorial / luxury real estate aesthetic:
 *   • Cream background, brand-50 / clay-50 room fills
 *   • Bold ink outer wall, charcoal interior walls
 *   • Subtle parquet pattern in living areas, tile pattern in bath
 *   • Minimal furniture stencils
 *   • Playfair Display for room names, Outfit for sizes
 */

export const FloorplanDiagram = ({ apartmentId, className = "" }) => {
    if (apartmentId === "erdgeschoss") return <ErdgeschossPlan className={className} />;
    if (apartmentId === "obergeschoss") return <ObergeschossPlan className={className} />;
    if (apartmentId === "penthouse") return <PenthousePlan className={className} />;
    return null;
};

/* ------------------------------------------------------------------ */
/*  Shared definitions – patterns, filters, styles                    */
/* ------------------------------------------------------------------ */
const SharedDefs = ({ id }) => (
    <defs>
        <pattern id={`parquet-${id}`} x="0" y="0" width="60" height="14" patternUnits="userSpaceOnUse">
            <rect width="60" height="14" fill="#FAF6F2" />
            <line x1="0" y1="14" x2="60" y2="14" stroke="#E5DCC9" strokeWidth="0.5" />
            <line x1="30" y1="0" x2="30" y2="14" stroke="#E5DCC9" strokeWidth="0.5" />
        </pattern>
        <pattern id={`tile-${id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <rect width="20" height="20" fill="#EAE3DD" />
            <path d="M0 0L20 0M0 0L0 20" stroke="#D5C7B8" strokeWidth="0.5" />
        </pattern>
        <pattern id={`deck-${id}`} x="0" y="0" width="22" height="180" patternUnits="userSpaceOnUse">
            <rect width="22" height="180" fill="#E8DCCB" />
            <line x1="22" y1="0" x2="22" y2="180" stroke="#C9B89F" strokeWidth="0.6" />
        </pattern>
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="110%" height="115%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
            <feOffset dx="0" dy="3" result="ofs" />
            <feFlood floodColor="#1A1A1A" floodOpacity="0.06" />
            <feComposite in2="ofs" operator="in" />
            <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
            </feMerge>
        </filter>
    </defs>
);

const NorthArrow = ({ x = 60, y = 60 }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="22" fill="#FFFFFF" stroke="#8C6A53" strokeWidth="1.2" />
        <path d="M 0,-12 L 6,7 L 0,3 L -6,7 Z" fill="#8C6A53" />
        <text y="38" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="10" fontWeight="600" fill="#735643" letterSpacing="3">N</text>
    </g>
);

const RoomLabel = ({ x, y, name, size, muted = false }) => (
    <g>
        <text x={x} y={y} textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif"
            fontSize={muted ? 17 : 21} fill={muted ? "#735643" : "#1A1A1A"} letterSpacing="0.5">
            {name}
        </text>
        <text x={x} y={y + 22} textAnchor="middle" fontFamily="'Outfit', sans-serif"
            fontSize="11" fontWeight="500" fill="#735643" letterSpacing="2.5">
            {(size || "").toUpperCase()}
        </text>
    </g>
);

const Window = ({ x1, y1, x2, y2 }) => {
    const horizontal = y1 === y2;
    const offset = horizontal ? 2 : 0;
    const offset2 = horizontal ? 0 : 2;
    return (
        <g>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FAF6F2" strokeWidth="9" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1A1A1A" strokeWidth="1.4" />
            <line x1={x1 + offset2} y1={y1 + offset} x2={x2 + offset2} y2={y2 + offset} stroke="#1A1A1A" strokeWidth="1.4" />
        </g>
    );
};

const DoorArc = ({ d }) => (
    <path d={d} fill="none" stroke="#A8A29E" strokeWidth="1" strokeDasharray="2 2" />
);

const FurnitureG = ({ children }) => (
    <g fill="none" stroke="#A8A29E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </g>
);

/* ================================================================== */
/*  ERDGESCHOSS – 78,75 m², 3 Zimmer + Terrasse                       */
/* ================================================================== */
const ErdgeschossPlan = ({ className = "" }) => (
    <svg
        viewBox="0 0 1180 1000"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-full h-auto ${className}`}
        role="img"
        aria-label="Stilisierter Grundriss Erdgeschosswohnung G21"
    >
        <SharedDefs id="eg" />
        <rect width="1180" height="1000" fill="#F9F8F6" />
        <NorthArrow x={1130} y={56} />

        {/* Outer building shell – building footprint approx 9m × 9m */}
        <g filter="url(#shadow-eg)">
            <rect x="60" y="80" width="900" height="800" fill="#FAF6F2" stroke="#1A1A1A" strokeWidth="8" strokeLinejoin="miter" />
        </g>

        {/* === Room fills === */}
        {/* WEST stack (3 rooms aligned to west wall) */}
        <rect x="66" y="86" width="320" height="316" fill="#F5F1EE" />               {/* Arbeitszimmer */}
        <rect x="66" y="408" width="320" height="234" fill="#F5F1EE" />               {/* Schlafzimmer */}
        <rect x="66" y="648" width="320" height="226" fill="#F0E6DC" />               {/* Küche */}
        {/* CENTRAL hallway (Diele) */}
        <rect x="392" y="86" width="160" height="556" fill="#FAF6F2" />               {/* Diele */}
        {/* TOP-RIGHT staircase + bath block */}
        <rect x="558" y="86" width="280" height="316" fill="#EAE3DD" />               {/* Treppenhaus */}
        <rect x="844" y="86" width="110" height="316" fill={`url(#tile-eg)`} />        {/* Bad */}
        {/* SOUTH + EAST: large Wohnen/Essen (L-shape) */}
        <rect x="392" y="648" width="562" height="226" fill={`url(#parquet-eg)`} />    {/* W/E south arm */}
        <rect x="558" y="408" width="396" height="234" fill={`url(#parquet-eg)`} />    {/* W/E east arm */}

        {/* === Internal walls === */}
        <g stroke="#2C302E" strokeWidth="3" strokeLinecap="square">
            {/* Vertical wall: West rooms | Central */}
            <line x1="389" y1="80" x2="389" y2="880" />
            {/* Horizontal divider: Arbeitszimmer | Schlafzimmer */}
            <line x1="60" y1="404" x2="389" y2="404" />
            {/* Horizontal divider: Schlafzimmer | Küche */}
            <line x1="60" y1="645" x2="389" y2="645" />
            {/* Vertical wall: Diele | Treppenhaus/Wohnen east */}
            <line x1="555" y1="80" x2="555" y2="645" />
            {/* Vertical wall: Treppenhaus | Bad */}
            <line x1="841" y1="80" x2="841" y2="404" />
            {/* Horizontal: Treppenhaus/Bad | Wohnen/Essen east arm */}
            <line x1="555" y1="404" x2="954" y2="404" />
            {/* Horizontal: Diele | Wohnen south arm */}
            <line x1="389" y1="645" x2="555" y2="645" />
        </g>

        {/* === Door swings === */}
        <DoorArc d="M 389 130 A 50 50 0 0 1 339 180" /> {/* Arbeit→Diele */}
        <DoorArc d="M 389 530 A 50 50 0 0 1 339 580" /> {/* Schlafen→Diele */}
        <DoorArc d="M 389 730 A 50 50 0 0 1 339 780" /> {/* Küche→Diele (open arch) */}
        <DoorArc d="M 555 200 A 50 50 0 0 0 505 250" /> {/* Diele→Treppenhaus */}
        <DoorArc d="M 555 480 A 50 50 0 0 0 505 530" /> {/* Diele→Wohnen */}
        <DoorArc d="M 870 404 A 40 40 0 0 1 910 364" /> {/* Bad door */}
        {/* Eingangstür from north (Treppenhaus side, north face) */}
        <DoorArc d="M 700 80 A 40 40 0 0 1 660 120" />

        {/* === Windows === */}
        {/* North face – Arbeitszimmer window */}
        <Window x1={120} y1={80} x2={340} y2={80} />
        {/* West face – Arbeitszimmer */}
        <Window x1={60} y1={150} x2={60} y2={340} />
        {/* West face – Schlafzimmer */}
        <Window x1={60} y1={440} x2={60} y2={620} />
        {/* West face – Küche */}
        <Window x1={60} y1={680} x2={60} y2={840} />
        {/* South face – Küche */}
        <Window x1={130} y1={880} x2={340} y2={880} />
        {/* South face – Wohnen/Essen (sliding door to Terrasse) */}
        <Window x1={460} y1={880} x2={900} y2={880} />
        {/* East face – Wohnen/Essen (sliding doors to Terrasse east arm) */}
        <Window x1={960} y1={460} x2={960} y2={840} />
        {/* North face – Treppenhaus tiny window */}
        <Window x1={620} y1={80} x2={780} y2={80} />

        {/* Bath fixtures, kitchen, furniture */}
        <FurnitureG>
            {/* ARBEITSZIMMER – desk + chair near north window */}
            <rect x="100" y="180" width="180" height="40" rx="2" />
            <rect x="160" y="140" width="60" height="36" rx="2" />
            <circle cx="200" cy="265" r="14" />
            {/* Bookshelf along east interior wall */}
            <rect x="340" y="140" width="36" height="180" />
            <line x1="340" y1="190" x2="376" y2="190" />
            <line x1="340" y1="240" x2="376" y2="240" />

            {/* SCHLAFZIMMER – king bed centered */}
            <rect x="120" y="450" width="200" height="155" rx="3" />
            <line x1="120" y1="475" x2="320" y2="475" />
            <rect x="80" y="450" width="34" height="40" rx="2" />
            <rect x="324" y="450" width="34" height="40" rx="2" />
            <rect x="140" y="490" width="74" height="42" rx="3" />
            <rect x="226" y="490" width="74" height="42" rx="3" />

            {/* KÜCHE – L-shaped kitchen counter (along S + E walls) */}
            <rect x="70" y="838" width="316" height="36" />
            <circle cx="120" cy="856" r="5" />
            <circle cx="148" cy="856" r="5" />
            <circle cx="176" cy="856" r="5" />
            <circle cx="204" cy="856" r="5" />
            <rect x="240" y="844" width="60" height="20" rx="1" />
            <rect x="350" y="660" width="36" height="180" />

            {/* DIELE - small bench */}
            <rect x="430" y="350" width="100" height="30" rx="2" />

            {/* TREPPENHAUS – stair pattern */}
            <g transform="translate(580,110)">
                <rect width="240" height="270" />
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <line key={i} x1="0" y1={20 + i * 27} x2="240" y2={20 + i * 27} />
                ))}
                <text x="120" y="260" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="9" fill="#A8A29E" letterSpacing="2">↑ OG</text>
            </g>

            {/* BAD – tub + sink + toilet */}
            <g transform="translate(854,110)">
                <rect width="86" height="48" rx="10" />
                <rect x="0" y="62" width="60" height="34" rx="3" />
                <circle cx="30" cy="79" r="3" />
                <ellipse cx="70" cy="200" rx="14" ry="20" />
                <rect x="56" y="178" width="28" height="10" rx="2" />
            </g>

            {/* WOHNEN/ESSEN – dining table + chairs (in east arm) + sofa+coffee table (in south arm) */}
            <g transform="translate(620,440)">
                <rect width="160" height="80" rx="4" />
                <rect x="-22" y="14" width="20" height="24" rx="2" />
                <rect x="-22" y="44" width="20" height="24" rx="2" />
                <rect x="162" y="14" width="20" height="24" rx="2" />
                <rect x="162" y="44" width="20" height="24" rx="2" />
                <rect x="20" y="-22" width="36" height="20" rx="2" />
                <rect x="100" y="-22" width="36" height="20" rx="2" />
            </g>
            <g transform="translate(450,700)">
                <rect width="200" height="80" rx="6" />
                <rect width="200" height="22" rx="3" />
                <rect x="240" y="20" width="120" height="50" rx="3" />
            </g>
        </FurnitureG>

        {/* Room labels */}
        <RoomLabel x={226} y={210} name="Arbeitszimmer" size="11,2 m²" />
        <RoomLabel x={226} y={510} name="Schlafzimmer" size="13,3 m²" />
        <RoomLabel x={226} y={750} name="Küche" size="9,1 m²" />
        <RoomLabel x={471} y={350} name="Diele" size="5,0 m²" muted />
        <RoomLabel x={696} y={230} name="Treppenhaus" size="EG · OG" muted />
        <RoomLabel x={898} y={220} name="Bad" size="gefliest" muted />
        <RoomLabel x={755} y={560} name="Wohnen / Essen" size="32,5 m²" />

        {/* === Terrasse east of building (extends out) === */}
        <g>
            <rect x="970" y="500" width="180" height="340" fill={`url(#deck-eg)`} stroke="#8C6A53" strokeWidth="1" strokeDasharray="6 4" />
            <text x="1060" y="680" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontStyle="italic" fontSize="20" fill="#735643" letterSpacing="6" transform="rotate(-90, 1060, 680)">
                Terrasse
            </text>
        </g>

        {/* Scale bar + metadata */}
        <g transform="translate(70,920)">
            <line x1="0" y1="0" x2="100" y2="0" stroke="#735643" strokeWidth="1.5" />
            <line x1="0" y1="-5" x2="0" y2="5" stroke="#735643" strokeWidth="1.5" />
            <line x1="100" y1="-5" x2="100" y2="5" stroke="#735643" strokeWidth="1.5" />
            <text x="50" y="22" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="2">ca. 1 m</text>
        </g>
        <text x="1140" y="940" textAnchor="end" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="3">
            G21 · ERDGESCHOSS · 78,75 m²
        </text>
    </svg>
);

/* ================================================================== */
/*  OBERGESCHOSS – 116,86 m², 4 Zimmer + Balkon                       */
/* ================================================================== */
const ObergeschossPlan = ({ className = "" }) => (
    <svg
        viewBox="0 0 1180 1100"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-full h-auto ${className}`}
        role="img"
        aria-label="Stilisierter Grundriss Obergeschosswohnung G21"
    >
        <SharedDefs id="og" />
        <rect width="1180" height="1100" fill="#F9F8F6" />
        <NorthArrow x={1130} y={56} />

        {/* Outer building shell (~9.4m × 10.5m – slightly taller than EG) */}
        <g filter="url(#shadow-og)">
            <rect x="60" y="80" width="900" height="940" fill="#FAF6F2" stroke="#1A1A1A" strokeWidth="8" strokeLinejoin="miter" />
        </g>

        {/* === Room fills === */}
        {/* WEST stack */}
        <rect x="66" y="86" width="320" height="240" fill="#F5F1EE" />              {/* Schlafzimmer (master) */}
        <rect x="66" y="332" width="320" height="280" fill="#F0E6DC" />              {/* Küche */}
        <rect x="66" y="618" width="320" height="396" fill="#F5F1EE" />              {/* Kinderzimmer (unten) */}
        {/* small Abstellraum, top-center adjacent to master bedroom */}
        <rect x="392" y="86" width="120" height="160" fill="#EAE3DD" />              {/* Abstellraum */}
        {/* Top-East: Kinderzimmer (oben) */}
        <rect x="638" y="86" width="316" height="220" fill="#F5F1EE" />              {/* Kinderzimmer (oben) */}
        {/* Center column: Flur + Bad + Treppenhaus */}
        <rect x="392" y="252" width="240" height="180" fill="#FAF6F2" />              {/* Flur upper */}
        <rect x="638" y="312" width="160" height="120" fill={`url(#tile-og)`} />      {/* Bad en-suite */}
        <rect x="804" y="312" width="150" height="120" fill={`url(#tile-og)`} />      {/* Bad Gäste / WC */}
        <rect x="392" y="438" width="406" height="170" fill="#EAE3DD" />              {/* Treppenhaus */}
        <rect x="804" y="438" width="150" height="170" fill="#FAF6F2" />              {/* Diele/entry zone */}
        {/* SOUTH: Wohnen/Essen with Balkon */}
        <rect x="392" y="614" width="562" height="400" fill={`url(#parquet-og)`} />   {/* Wohnen/Essen */}

        {/* === Internal walls === */}
        <g stroke="#2C302E" strokeWidth="3" strokeLinecap="square">
            {/* Vertical: West rooms | Center */}
            <line x1="389" y1="80" x2="389" y2="1020" />
            {/* Horizontal: Schlafen | Küche */}
            <line x1="60" y1="329" x2="389" y2="329" />
            {/* Horizontal: Küche | Kinderzimmer (unten) */}
            <line x1="60" y1="615" x2="389" y2="615" />
            {/* Vertical: Abstell | (right) */}
            <line x1="515" y1="80" x2="515" y2="249" />
            {/* Vertical: Top-center gap | Kinderzimmer (oben) */}
            <line x1="635" y1="80" x2="635" y2="309" />
            {/* Horizontal: Top band | mid */}
            <line x1="389" y1="249" x2="635" y2="249" />
            <line x1="635" y1="309" x2="954" y2="309" />
            {/* Horizontal: Mid bath row | Treppenhaus row */}
            <line x1="389" y1="435" x2="954" y2="435" />
            {/* Horizontal: Center mid | Wohnen/Essen */}
            <line x1="389" y1="611" x2="954" y2="611" />
            {/* Vertical: Bad | WC */}
            <line x1="801" y1="312" x2="801" y2="435" />
            {/* Vertical: Bad division left edge */}
            <line x1="635" y1="309" x2="635" y2="435" />
            {/* Vertical: Treppenhaus | Diele entry */}
            <line x1="801" y1="438" x2="801" y2="611" />
        </g>

        {/* === Door swings === */}
        <DoorArc d="M 389 160 A 50 50 0 0 1 339 210" /> {/* Schlafen door */}
        <DoorArc d="M 460 249 A 40 40 0 0 1 500 209" /> {/* Abstell door */}
        <DoorArc d="M 389 470 A 50 50 0 0 1 339 520" /> {/* Küche door */}
        <DoorArc d="M 389 750 A 50 50 0 0 1 339 800" /> {/* Kinderzimmer (unten) door */}
        <DoorArc d="M 700 309 A 50 50 0 0 1 700 259" /> {/* Kinderzimmer (oben) door */}
        <DoorArc d="M 700 435 A 40 40 0 0 1 660 395" /> {/* Bad door */}
        <DoorArc d="M 870 435 A 40 40 0 0 1 830 395" /> {/* WC door */}
        <DoorArc d="M 600 611 A 60 60 0 0 1 540 671" /> {/* Wohnen door */}
        {/* Entry from outside (east) into Treppenhaus */}
        <DoorArc d="M 954 500 A 60 60 0 0 1 894 560" />

        {/* === Windows === */}
        {/* North: Schlafen + Abstell + Kinderzimmer (oben) */}
        <Window x1={130} y1={80} x2={340} y2={80} />
        <Window x1={418} y1={80} x2={490} y2={80} />
        <Window x1={690} y1={80} x2={910} y2={80} />
        {/* West: Schlafen + Küche + Kinderzimmer (unten) */}
        <Window x1={60} y1={140} x2={60} y2={290} />
        <Window x1={60} y1={380} x2={60} y2={580} />
        <Window x1={60} y1={680} x2={60} y2={970} />
        {/* East: Kinderzimmer (oben) + Diele */}
        <Window x1={960} y1={140} x2={960} y2={280} />
        {/* East lower: Wohnen */}
        <Window x1={960} y1={680} x2={960} y2={870} />
        {/* South: Wohnen + Balkon glass */}
        <Window x1={130} y1={1020} x2={400} y2={1020} />
        <Window x1={500} y1={1020} x2={900} y2={1020} />

        {/* === Furniture === */}
        <FurnitureG>
            {/* SCHLAFZIMMER – king bed + nightstands */}
            <g transform="translate(110,130)">
                <rect x="0" y="40" width="200" height="150" rx="3" />
                <line x1="0" y1="62" x2="200" y2="62" />
                <rect x="-30" y="40" width="26" height="44" rx="2" />
                <rect x="204" y="40" width="26" height="44" rx="2" />
                <rect x="20" y="78" width="80" height="44" rx="3" />
                <rect x="100" y="78" width="80" height="44" rx="3" />
            </g>
            {/* ABSTELLRAUM – simple shelves */}
            <g transform="translate(404,108)">
                <line x1="0" y1="20" x2="100" y2="20" />
                <line x1="0" y1="60" x2="100" y2="60" />
                <line x1="0" y1="100" x2="100" y2="100" />
            </g>
            {/* KÜCHE – galley layout */}
            <g transform="translate(70,348)">
                <rect width="280" height="32" />
                <circle cx="60" cy="16" r="5" />
                <circle cx="80" cy="16" r="5" />
                <circle cx="100" cy="16" r="5" />
                <circle cx="120" cy="16" r="5" />
                <rect x="180" y="6" width="60" height="20" rx="1" />
                <rect width="32" height="240" />
                <rect x="80" y="180" width="180" height="60" rx="3" />
            </g>
            {/* KINDERZIMMER (oben) – single bed + desk */}
            <g transform="translate(680,110)">
                <rect width="120" height="190" rx="3" />
                <rect width="120" height="22" rx="3" />
                <rect x="200" y="0" width="80" height="40" rx="2" />
                <circle cx="240" cy="80" r="14" />
            </g>
            {/* KINDERZIMMER (unten) – bed + desk + wardrobe */}
            <g transform="translate(110,650)">
                <rect width="180" height="100" rx="3" />
                <rect width="180" height="22" rx="3" />
                <rect x="0" y="160" width="120" height="40" rx="2" />
                <circle cx="60" cy="240" r="14" />
                <rect x="0" y="320" width="280" height="40" />
                <line x1="100" y1="320" x2="100" y2="360" />
                <line x1="200" y1="320" x2="200" y2="360" />
            </g>
            {/* BAD en-suite – tub + sink + toilet */}
            <g transform="translate(648,330)">
                <rect width="100" height="44" rx="10" />
                <rect x="0" y="60" width="60" height="36" rx="3" />
                <circle cx="30" cy="78" r="3" />
                <ellipse cx="120" cy="80" rx="14" ry="20" />
                <rect x="106" y="58" width="28" height="10" rx="2" />
            </g>
            {/* Bad Gäste / WC – sink + toilet */}
            <g transform="translate(820,338)">
                <ellipse cx="40" cy="40" rx="14" ry="20" />
                <rect x="26" y="18" width="28" height="10" rx="2" />
                <rect x="80" y="40" width="50" height="36" rx="3" />
            </g>
            {/* TREPPENHAUS – stair pattern */}
            <g transform="translate(420,460)">
                <rect width="320" height="130" />
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <line key={i} x1="0" y1={20 + i * 19} x2="320" y2={20 + i * 19} />
                ))}
                <text x="160" y="120" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="9" fill="#A8A29E" letterSpacing="2">↓ EG · ↑ DG</text>
            </g>
            {/* WOHNEN/ESSEN – dining + sofa */}
            <g transform="translate(450,650)">
                <rect width="200" height="100" rx="4" />
                <rect x="-22" y="14" width="20" height="24" rx="2" />
                <rect x="-22" y="48" width="20" height="24" rx="2" />
                <rect x="-22" y="76" width="20" height="24" rx="2" />
                <rect x="202" y="14" width="20" height="24" rx="2" />
                <rect x="202" y="48" width="20" height="24" rx="2" />
                <rect x="202" y="76" width="20" height="24" rx="2" />
                <rect x="40" y="-22" width="36" height="20" rx="2" />
                <rect x="120" y="-22" width="36" height="20" rx="2" />
            </g>
            <g transform="translate(450,830)">
                <rect width="220" height="90" rx="6" />
                <rect width="220" height="22" rx="3" />
                <rect x="270" y="20" width="100" height="50" rx="3" />
            </g>
        </FurnitureG>

        {/* === Room labels === */}
        <RoomLabel x={226} y={200} name="Schlafzimmer" size="16,0 m²" />
        <RoomLabel x={452} y={170} name="Abstell" size="2,5 m²" muted />
        <RoomLabel x={226} y={460} name="Küche" size="11,5 m²" />
        <RoomLabel x={226} y={810} name="Kinderzimmer" size="14,5 m²" />
        <RoomLabel x={796} y={195} name="Kinderzimmer" size="14,0 m²" />
        <RoomLabel x={511} y={345} name="Flur" size="5,0 m²" muted />
        <RoomLabel x={715} y={385} name="Bad" size="5,0 m²" muted />
        <RoomLabel x={877} y={385} name="WC" size="4,5 m²" muted />
        <RoomLabel x={595} y={528} name="Treppenhaus" size="EG · DG" muted />
        <RoomLabel x={877} y={528} name="Diele" size="" muted />
        <RoomLabel x={673} y={810} name="Wohnen / Essen" size="28,3 m²" />

        {/* === Balkon south of building (extends slightly) === */}
        <g>
            <rect x="500" y="1024" width="400" height="60" fill={`url(#deck-og)`} stroke="#8C6A53" strokeWidth="1" strokeDasharray="6 4" />
            <text x="700" y="1062" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontStyle="italic" fontSize="18" fill="#735643" letterSpacing="6">
                Balkon · 4,5 m²
            </text>
        </g>

        {/* Scale bar + metadata */}
        <g transform="translate(70,1075)">
            <line x1="0" y1="0" x2="100" y2="0" stroke="#735643" strokeWidth="1.5" />
            <line x1="0" y1="-5" x2="0" y2="5" stroke="#735643" strokeWidth="1.5" />
            <line x1="100" y1="-5" x2="100" y2="5" stroke="#735643" strokeWidth="1.5" />
            <text x="50" y="20" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="2">ca. 1 m</text>
        </g>
        <text x="1140" y="1095" textAnchor="end" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="3">
            G21 · OBERGESCHOSS · 116,86 m²
        </text>
    </svg>
);

/* ================================================================== */
/*  PENTHOUSE / DG – 114,27 m², 3 Zimmer + Dachterrasse               */
/* ================================================================== */
const PenthousePlan = ({ className = "" }) => (
    <svg
        viewBox="0 0 1300 1100"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-full h-auto ${className}`}
        role="img"
        aria-label="Stilisierter Grundriss Penthousewohnung G21"
    >
        <SharedDefs id="ph" />
        <rect width="1300" height="1100" fill="#F9F8F6" />
        <NorthArrow x={1250} y={56} />

        {/* Outer building shell – ~10m × 9.5m main area */}
        <g filter="url(#shadow-ph)">
            <rect x="60" y="80" width="900" height="940" fill="#FAF6F2" stroke="#1A1A1A" strokeWidth="8" strokeLinejoin="miter" />
        </g>

        {/* === Room fills === */}
        {/* TOP band: Schlafen (NW) | Arbeiten (NE) */}
        <rect x="66" y="86" width="430" height="380" fill="#F5F1EE" />               {/* Schlafen */}
        <rect x="502" y="86" width="452" height="380" fill="#F5F1EE" />               {/* Arbeiten */}
        {/* CENTER row: en-suite Bad | Flur | Treppenhaus | WC */}
        <rect x="66" y="472" width="240" height="180" fill={`url(#tile-ph)`} />       {/* Bad */}
        <rect x="312" y="472" width="180" height="180" fill="#FAF6F2" />              {/* Flur/Diele */}
        <rect x="498" y="472" width="280" height="180" fill="#EAE3DD" />              {/* Treppenhaus */}
        <rect x="784" y="472" width="170" height="180" fill={`url(#tile-ph)`} />      {/* WC */}
        {/* BOTTOM large: open Wohnen/Essen + integrated Küche */}
        <rect x="66" y="658" width="888" height="356" fill={`url(#parquet-ph)`} />    {/* Wohnen/Essen open */}

        {/* === Internal walls === */}
        <g stroke="#2C302E" strokeWidth="3" strokeLinecap="square">
            {/* Top: Schlafen | Arbeiten */}
            <line x1="499" y1="80" x2="499" y2="466" />
            {/* Top | Mid */}
            <line x1="60" y1="469" x2="954" y2="469" />
            {/* Mid: Bad | Flur */}
            <line x1="309" y1="469" x2="309" y2="655" />
            {/* Mid: Flur | Treppenhaus */}
            <line x1="495" y1="469" x2="495" y2="655" />
            {/* Mid: Treppenhaus | WC */}
            <line x1="781" y1="469" x2="781" y2="655" />
            {/* Mid | Bottom */}
            <line x1="60" y1="655" x2="954" y2="655" />
        </g>

        {/* === Door swings === */}
        <DoorArc d="M 200 469 A 50 50 0 0 1 200 419" /> {/* Schlafen door */}
        <DoorArc d="M 309 540 A 40 40 0 0 1 269 580" /> {/* Bad door */}
        <DoorArc d="M 700 469 A 50 50 0 0 1 700 419" /> {/* Arbeiten door */}
        <DoorArc d="M 495 540 A 40 40 0 0 1 535 580" /> {/* Flur→Treppenhaus */}
        <DoorArc d="M 781 540 A 40 40 0 0 0 821 580" /> {/* WC door */}
        <DoorArc d="M 400 655 A 60 60 0 0 1 460 715" /> {/* Flur→Wohnen */}
        {/* Sliding door from Wohnen to Dachterrasse (south + east) */}

        {/* === Windows === */}
        {/* North: Schlafen + Arbeiten */}
        <Window x1={130} y1={80} x2={460} y2={80} />
        <Window x1={540} y1={80} x2={910} y2={80} />
        {/* West: Schlafen + Wohnen */}
        <Window x1={60} y1={140} x2={60} y2={420} />
        <Window x1={60} y1={720} x2={60} y2={970} />
        {/* East: Arbeiten + Wohnen */}
        <Window x1={960} y1={140} x2={960} y2={420} />
        {/* South: large glass to Dachterrasse (multiple sliding doors) */}
        <Window x1={130} y1={1020} x2={620} y2={1020} />

        {/* === Furniture === */}
        <FurnitureG>
            {/* SCHLAFEN – king bed + 2 nightstands + bench */}
            <g transform="translate(140,160)">
                <rect x="0" y="40" width="240" height="180" rx="4" />
                <line x1="0" y1="65" x2="240" y2="65" />
                <rect x="-30" y="40" width="26" height="48" rx="2" />
                <rect x="244" y="40" width="26" height="48" rx="2" />
                <rect x="20" y="80" width="80" height="44" rx="3" />
                <rect x="140" y="80" width="80" height="44" rx="3" />
                <rect x="40" y="240" width="160" height="32" rx="3" />
            </g>
            {/* ARBEITEN – desk + chair + bookshelf */}
            <g transform="translate(580,160)">
                <rect width="220" height="48" rx="2" />
                <rect x="80" y="-30" width="60" height="30" rx="2" />
                <circle cx="110" cy="80" r="16" />
                <rect x="320" y="0" width="40" height="220" />
                <line x1="320" y1="55" x2="360" y2="55" />
                <line x1="320" y1="110" x2="360" y2="110" />
                <line x1="320" y1="165" x2="360" y2="165" />
            </g>
            {/* BAD – tub + sink + toilet */}
            <g transform="translate(80,490)">
                <rect width="180" height="56" rx="14" />
                <rect x="0" y="80" width="120" height="40" rx="3" />
                <circle cx="40" cy="100" r="3" />
                <circle cx="80" cy="100" r="3" />
                <ellipse cx="220" cy="120" rx="20" ry="26" />
                <rect x="200" y="92" width="40" height="14" rx="2" />
            </g>
            {/* WC – toilet + tiny sink */}
            <g transform="translate(810,510)">
                <ellipse cx="40" cy="40" rx="20" ry="26" />
                <rect x="20" y="12" width="40" height="14" rx="2" />
                <rect x="0" y="100" width="60" height="32" rx="3" />
            </g>
            {/* TREPPENHAUS – stair pattern */}
            <g transform="translate(516,490)">
                <rect width="240" height="150" />
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <line key={i} x1="0" y1={15 + i * 19} x2="240" y2={15 + i * 19} />
                ))}
                <text x="120" y="140" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="9" fill="#A8A29E" letterSpacing="2">↓ OG</text>
            </g>
            {/* WOHNEN/ESSEN – dining + sofa + KAMIN + integrated kitchen island */}
            <g transform="translate(110,690)">
                {/* Kitchen counter along west + island */}
                <rect x="0" y="0" width="40" height="240" />
                <circle cx="20" cy="40" r="5" />
                <circle cx="20" cy="60" r="5" />
                <circle cx="20" cy="80" r="5" />
                <circle cx="20" cy="100" r="5" />
                <rect x="6" y="160" width="28" height="34" rx="2" />
                {/* Kitchen island */}
                <rect x="80" y="80" width="200" height="50" rx="3" />
                {/* Dining table */}
                <rect x="320" y="20" width="180" height="90" rx="4" />
                <rect x="300" y="34" width="20" height="24" rx="2" />
                <rect x="300" y="76" width="20" height="24" rx="2" />
                <rect x="500" y="34" width="20" height="24" rx="2" />
                <rect x="500" y="76" width="20" height="24" rx="2" />
                <rect x="340" y="-2" width="36" height="20" rx="2" />
                <rect x="430" y="-2" width="36" height="20" rx="2" />
                {/* Sofa + coffee table */}
                <rect x="540" y="40" width="240" height="80" rx="6" />
                <rect x="540" y="40" width="240" height="20" rx="3" />
                <rect x="600" y="140" width="120" height="50" rx="3" />
                {/* KAMIN (fireplace) – along south wall */}
                <rect x="200" y="270" width="100" height="30" rx="2" />
                <rect x="230" y="240" width="40" height="30" rx="1" />
                <text x="250" y="316" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="9" fill="#A8A29E" letterSpacing="2">KAMIN</text>
            </g>
        </FurnitureG>

        {/* === Room labels === */}
        <RoomLabel x={281} y={280} name="Schlafzimmer" size="18,8 m²" />
        <RoomLabel x={728} y={280} name="Arbeiten" size="17,8 m²" />
        <RoomLabel x={186} y={555} name="Bad" size="8,7 m²" muted />
        <RoomLabel x={401} y={555} name="Flur" size="6,7 m²" muted />
        <RoomLabel x={638} y={555} name="Treppenhaus" size="DG · OG" muted />
        <RoomLabel x={868} y={555} name="WC" size="4,7 m²" muted />
        <RoomLabel x={510} y={840} name="Wohnen / Essen / Küche" size="45,4 m² · offen mit Kamin" />

        {/* === Dachterrasse SE corner (extends east + south) === */}
        <g>
            <rect x="970" y="600" width="280" height="420" fill={`url(#deck-ph)`} stroke="#8C6A53" strokeWidth="1" strokeDasharray="6 4" />
            <text x="1110" y="800" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontStyle="italic" fontSize="20" fill="#735643" letterSpacing="6" transform="rotate(-90, 1110, 800)">
                Dachterrasse
            </text>
            <text x="1110" y="830" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="2" transform="rotate(-90, 1110, 830)">
                18,1 m² (50 % angerechnet)
            </text>
        </g>
        {/* South extension of terrace */}
        <g>
            <rect x="640" y="1024" width="320" height="60" fill={`url(#deck-ph)`} stroke="#8C6A53" strokeWidth="1" strokeDasharray="6 4" />
        </g>

        {/* Scale bar + metadata */}
        <g transform="translate(70,1075)">
            <line x1="0" y1="0" x2="100" y2="0" stroke="#735643" strokeWidth="1.5" />
            <line x1="0" y1="-5" x2="0" y2="5" stroke="#735643" strokeWidth="1.5" />
            <line x1="100" y1="-5" x2="100" y2="5" stroke="#735643" strokeWidth="1.5" />
            <text x="50" y="20" textAnchor="middle" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="2">ca. 1 m</text>
        </g>
        <text x="1260" y="1095" textAnchor="end" fontFamily="'Outfit', sans-serif" fontSize="10" fill="#735643" letterSpacing="3">
            G21 · DACHGESCHOSS · 114,27 m²
        </text>
    </svg>
);

